<template>
	<div class="content">
		<div class="section">
			<div class="section-tit">접속·활동이력</div>

			<div class="txt-c--grey mb-1">
				계정(ID) 기준으로 접속일시와 프로그램 내 활동(자료 조회·출력·다운로드·변경)을 확인합니다.
				외부 제휴기관(신한은행·대신증권 등) 계정도 같은 기준으로 기록됩니다.
			</div>

			<!-- ── 공통 필터 ─────────────────────────────────────────────── -->
			<div class="table-wrap">
				<table class="table">
					<colgroup>
						<col width="10%" /><col width="40%" /><col width="10%" /><col width="40%" />
					</colgroup>
					<tr>
						<th>기간</th>
						<td>
							<input type="date" class="form-control d-inline-block" style="width: 150px" v-model="from" />
							~
							<input type="date" class="form-control d-inline-block" style="width: 150px" v-model="to" />
							<button type="button" class="btn btn-xsm btn-secondary ms-1" @click="setRange(0)">오늘</button>
							<button type="button" class="btn btn-xsm btn-secondary" @click="setRange(6)">7일</button>
							<button type="button" class="btn btn-xsm btn-secondary" @click="setRange(29)">30일</button>
							<button type="button" class="btn btn-xsm btn-secondary" @click="setRange(89)">90일</button>
						</td>
						<th>계정</th>
						<td>
							<select class="form-select d-inline-block" style="width: 220px" v-model="memUid">
								<option value="">전체 계정</option>
								<option v-for="m in members" :key="'mem_' + m.mem_uid" :value="m.mem_uid">
									{{ m.mem_id || '(ID 없음)' }} ({{ m.mem_name || '-' }}){{ memberSuffix(m) }}
								</option>
							</select>
							<input
								type="text"
								class="form-control d-inline-block ms-1"
								style="width: 160px"
								placeholder="계정ID 직접검색"
								v-model="memId"
								@keyup.enter="search"
							/>
						</td>
					</tr>
					<tr>
						<th>소속</th>
						<td>
							<select class="form-select d-inline-block" style="width: 200px" v-model="institution">
								<option value="">전체</option>
								<option value="internal">내부 직원만</option>
								<option value="external">외부 제휴기관만</option>
								<option value="shinhan">신한은행</option>
								<option value="daishin">대신증권</option>
							</select>
						</td>
						<th>접속IP</th>
						<td>
							<input
								type="text"
								class="form-control d-inline-block"
								style="width: 200px"
								placeholder="IP 일부 검색"
								v-model="ip"
								@keyup.enter="search"
							/>
							<button type="button" class="btn btn-sm btn-primary ms-2" @click="search" :disabled="loading">
								{{ loading ? '조회 중…' : '조회' }}
							</button>
							<button type="button" class="btn btn-sm btn-secondary ms-1" @click="resetFilter">초기화</button>
						</td>
					</tr>
				</table>
			</div>

			<div class="tab-btn mt-2">
				<button type="button" class="btn tab" v-bind:class="{ active: tab === 'summary' }" @click="changeTab('summary')">
					계정별 요약
				</button>
				<button type="button" class="btn tab" v-bind:class="{ active: tab === 'access' }" @click="changeTab('access')">
					접속기록
				</button>
				<button type="button" class="btn tab" v-bind:class="{ active: tab === 'activity' }" @click="changeTab('activity')">
					활동내역
				</button>
			</div>

			<!-- ── 계정별 요약 ───────────────────────────────────────────── -->
			<div class="tab-con" v-if="tab === 'summary'">
				<div class="txt-c--grey mb-1">
					{{ from }} ~ {{ to }} 기준 · 계정 {{ summary.length }}개
					<span v-if="unknownFail" class="txt-c--red">
						· 존재하지 않는 ID 로그인 시도 {{ unknownFail }}건 (접속기록 탭에서 확인)
					</span>
					<span> · 행을 클릭하면 그 계정의 접속기록으로 이동합니다.</span>
				</div>
				<div class="table-wrap">
					<table class="table click">
						<tr>
							<th>계정ID</th>
							<th>이름</th>
							<th>직급</th>
							<th>팀</th>
							<th>소속</th>
							<th>재직</th>
							<th>최근 접속일시</th>
							<th>최근 IP</th>
							<th>로그인</th>
							<th>열람</th>
							<th>연락처 확인</th>
							<th>출력·다운</th>
							<th>변경</th>
							<th>거부·실패</th>
						</tr>
						<tr v-for="s in summary" :key="'sum_' + s.mem_uid" @click="drillDown(s)">
							<td>{{ s.mem_id || '-' }}</td>
							<td>{{ s.mem_name || '-' }}<span v-if="s.deleted === 'Y'"> (삭제된 계정)</span></td>
							<td>{{ s.mem_rank_name || '-' }}</td>
							<td>{{ s.team_name || '-' }}</td>
							<td>{{ instLabel(s.institution) }}</td>
							<td>{{ s.hire_status === 'N' ? '퇴사' : s.hire_status === 'Y' ? '재직' : '-' }}</td>
							<td>{{ fmtDt(s.last_login_at) || '기록 없음' }}</td>
							<td>{{ s.last_ip || '-' }}</td>
							<td>{{ s.login_cnt }}</td>
							<td>{{ s.view_cnt }}</td>
							<td>{{ s.owner_cnt }}</td>
							<td>{{ s.print_cnt }}</td>
							<td>{{ s.change_cnt }}</td>
							<td v-bind:class="{ 'txt-c--red': s.deny_cnt > 0 }">{{ s.deny_cnt }}</td>
						</tr>
						<tr v-if="!summary.length && !loading">
							<td colspan="14">해당 기간에 기록이 없습니다.</td>
						</tr>
					</table>
				</div>
			</div>

			<!-- ── 접속기록 ──────────────────────────────────────────────── -->
			<div class="tab-con" v-if="tab === 'access'">
				<div class="mb-1">
					<label class="input-checkbox" v-for="a in accessActions" :key="'aa_' + a.key">
						<input type="radio" :value="a.key" v-model="accessAction" @change="getAccess(1)" />
						<span class="text">{{ a.label }}</span>
					</label>
					<button type="button" class="btn btn-xsm btn-secondary ms-2" @click="exportCsv('access')">CSV 내려받기</button>
					<span class="txt-c--grey ms-2">총 {{ accessTotal }}건</span>
				</div>
				<div class="table-wrap">
					<table class="table">
						<tr>
							<th>일시</th>
							<th>계정ID</th>
							<th>이름</th>
							<th>구분</th>
							<th>접속IP</th>
							<th>기기</th>
							<th>소속</th>
							<th>비고</th>
						</tr>
						<tr v-for="r in accessList" :key="'acc_' + r.audit_uid">
							<td>{{ fmtDt(r.created_at) }}</td>
							<td>{{ r.mem_id || '-' }}</td>
							<td>{{ r.mem_name || '(미상)' }}</td>
							<td v-bind:class="{ 'txt-c--red': r.action !== 'login' && r.action !== 'logout' }">
								{{ actionLabel(r.action) }}
							</td>
							<td>{{ r.ip || '-' }}</td>
							<td>{{ machineLabel(r.machine) }}</td>
							<td>{{ instLabel(r.institution) }}{{ r.guest_flag === 'Y' ? ' · 게스트' : '' }}</td>
							<td>{{ r.detail || '-' }}</td>
						</tr>
						<tr v-if="!accessList.length && !loading">
							<td colspan="8">해당 조건의 접속기록이 없습니다.</td>
						</tr>
					</table>
				</div>
				<ul class="pagination b-pagination pagination-sm" v-if="accessPage != null && accessPage.list.length">
					<li class="page-item" v-bind:class="{ disabled: 1 == accessPg }">
						<button type="button" class="page-link" @click="getAccess(1)"><b-icon-chevron-double-left /></button>
					</li>
					<li class="page-item" v-bind:class="{ disabled: accessPage.first == null }">
						<button type="button" class="page-link" @click="accessPage.first !== null ? getAccess(accessPage.first) : ''">
							<b-icon-chevron-left />
						</button>
					</li>
					<li
						v-for="pg in accessPage.list"
						:key="'apg_' + pg"
						class="page-item"
						v-bind:class="{ active: accessPage.currentPage == pg }"
					>
						<button type="button" class="page-link" @click="getAccess(pg)">{{ pg }}</button>
					</li>
					<li class="page-item" v-bind:class="{ disabled: accessPage.end == null }">
						<button type="button" class="page-link" @click="accessPage.end !== null ? getAccess(accessPage.end) : ''">
							<b-icon-chevron-right />
						</button>
					</li>
					<li class="page-item" v-bind:class="{ disabled: accessPage.totalPage == accessPg }">
						<button type="button" class="page-link" @click="getAccess(accessPage.totalPage)">
							<b-icon-chevron-double-right />
						</button>
					</li>
				</ul>
			</div>

			<!-- ── 활동내역 ──────────────────────────────────────────────── -->
			<div class="tab-con" v-if="tab === 'activity'">
				<div class="mb-1">
					<select class="form-select d-inline-block" style="width: 170px" v-model="activityAction" @change="getActivity(1)">
						<option value="">활동 전체</option>
						<option v-for="a in activityActions" :key="'va_' + a.key" :value="a.key">{{ a.label }}</option>
					</select>
					<select class="form-select d-inline-block ms-1" style="width: 170px" v-model="targetType" @change="getActivity(1)">
						<option value="">대상 전체</option>
						<option v-for="t in targetTypes" :key="'tt_' + t.key" :value="t.key">{{ t.label }}</option>
					</select>
					<button type="button" class="btn btn-xsm btn-secondary ms-2" @click="exportCsv('activity')">CSV 내려받기</button>
					<span class="txt-c--grey ms-2">총 {{ activityTotal }}건</span>
				</div>
				<div class="table-wrap">
					<table class="table">
						<tr>
							<th>일시</th>
							<th>계정ID</th>
							<th>이름</th>
							<th>활동</th>
							<th>대상종류</th>
							<th>대상</th>
							<th>내용</th>
							<th>접속IP</th>
							<th>소속</th>
						</tr>
						<tr v-for="r in activityList" :key="'act_' + r.audit_uid">
							<td>{{ fmtDt(r.created_at) }}</td>
							<td>{{ r.mem_id || '-' }}</td>
							<td>{{ r.mem_name || '(미상)' }}</td>
							<td v-bind:class="{ 'txt-c--red': r.action === 'deny' }">{{ actionLabel(r.action) }}</td>
							<td>{{ targetLabel(r.target_type) }}</td>
							<td>
								<button
									v-if="canOpen(r)"
									type="button"
									class="btn btn-link p-0"
									@click="openTarget(r)"
									:title="r.target_sub || ''"
								>
									{{ r.target_name || '#' + r.target_uid }}
								</button>
								<span v-else>{{ r.target_name || (r.target_uid ? '#' + r.target_uid : '-') }}</span>
							</td>
							<td>{{ r.detail || '-' }}</td>
							<td>{{ r.ip || '-' }}</td>
							<td>{{ instLabel(r.institution) }}{{ r.guest_flag === 'Y' ? ' · 게스트' : '' }}</td>
						</tr>
						<tr v-if="!activityList.length && !loading">
							<td colspan="9">해당 조건의 활동내역이 없습니다.</td>
						</tr>
					</table>
				</div>
				<ul class="pagination b-pagination pagination-sm" v-if="activityPage != null && activityPage.list.length">
					<li class="page-item" v-bind:class="{ disabled: 1 == activityPg }">
						<button type="button" class="page-link" @click="getActivity(1)"><b-icon-chevron-double-left /></button>
					</li>
					<li class="page-item" v-bind:class="{ disabled: activityPage.first == null }">
						<button type="button" class="page-link" @click="activityPage.first !== null ? getActivity(activityPage.first) : ''">
							<b-icon-chevron-left />
						</button>
					</li>
					<li
						v-for="pg in activityPage.list"
						:key="'vpg_' + pg"
						class="page-item"
						v-bind:class="{ active: activityPage.currentPage == pg }"
					>
						<button type="button" class="page-link" @click="getActivity(pg)">{{ pg }}</button>
					</li>
					<li class="page-item" v-bind:class="{ disabled: activityPage.end == null }">
						<button type="button" class="page-link" @click="activityPage.end !== null ? getActivity(activityPage.end) : ''">
							<b-icon-chevron-right />
						</button>
					</li>
					<li class="page-item" v-bind:class="{ disabled: activityPage.totalPage == activityPg }">
						<button type="button" class="page-link" @click="getActivity(activityPage.totalPage)">
							<b-icon-chevron-double-right />
						</button>
					</li>
				</ul>
			</div>

			<div class="txt-c--grey mt-2">
				※ 기록 대상: 로그인·로그아웃·로그인 실패/차단, 매물·고객·계약·게시글 상세 조회, 출력(연쇄출력 포함),
				첨부파일 다운로드, 매물·고객·계약·직원계정·환경설정 변경, 문자 발송, 권한 없는 접근 시도.
				지도 탐색·목록 검색처럼 건수가 과도한 조회는 기록하지 않습니다(실제 자료 열람 시점만 남깁니다).<br />
				요약의 '열람' 은 상세 화면을 연 횟수입니다. 매물 상세는 한 번 열 때 소유주 메모·상담로그·작업이력을 함께 불러
				활동내역에는 4건으로 남지만, 열람 수에는 1회로 셉니다. '연락처 확인' 은 조회사유를 적고 소유주 연락처나 매수고객 연락처를 연 횟수입니다.
				매물 상세는 계정별로 5분 250건을 넘으면 거부되고 '권한거부' 로 남습니다. 하루 열람 수는 거부하지 않고 1,000건마다 관리자에게 알림만 보냅니다.
			</div>
		</div>
	</div>
</template>

<script>
// 활동 유형·대상 종류 라벨은 백엔드(routes/audit.js)의 ACTION_LABEL / TARGET_LABEL 과 같은 값을 쓴다.
// (CSV 와 화면 표기가 어긋나면 같은 이력을 두 이름으로 부르게 된다.)
const ACTION_LABEL = {
	login: '로그인',
	login_fail: '로그인 실패',
	login_block: '접속 차단',
	logout: '로그아웃',
	view: '조회',
	print: '출력',
	download: '다운로드',
	create: '등록',
	update: '수정',
	delete: '삭제',
	send: '발송',
	deny: '권한거부',
};

const TARGET_LABEL = {
	building_sell: '매매 매물',
	building_rent: '임대 매물',
	client: '매수고객',
	rent_client: '임대고객',
	contract: '계약',
	board: '게시글',
	mem: '직원계정',
	setting: '환경설정',
	sms: '문자',
	owner_log: '소유자정보',
	post: '쪽지',
	partner_request: '제휴요청',
	audit: '접속·활동이력',
};

// 대상 클릭 시 열 화면. 상대경로로 열면 /X/:id 형태의 상세화면에서 빈 화면이 되므로 항상 앞 슬래시.
const TARGET_ROUTE = {
	building_sell: '/MapDetail/',
	building_rent: '/LeaseMapDetail/',
	client: '/UserDetail/',
	rent_client: '/LeaseUserDetail/',
	contract: '/ContractDetail/',
	board: '/BoardDetail/',
};

export default {
	name: 'SystemAdmin11',
	components: {},
	data() {
		return {
			tab: 'summary',
			loading: false,

			// 필터
			from: '',
			to: '',
			memUid: '',
			memId: '',
			institution: '',
			ip: '',

			members: [],

			// 계정별 요약
			summary: [],
			unknownFail: 0,

			// 접속기록
			accessAction: '',
			accessList: [],
			accessTotal: 0,
			accessPg: 1,
			accessPage: null,

			// 활동내역
			activityAction: '',
			targetType: '',
			activityList: [],
			activityTotal: 0,
			activityPg: 1,
			activityPage: null,

			itemSize: 30,
			blockSize: 5,

			accessActions: [
				{ key: '', label: '전체' },
				{ key: 'login', label: '로그인' },
				{ key: 'logout', label: '로그아웃' },
				{ key: 'login_fail', label: '로그인 실패' },
				{ key: 'login_block', label: '접속 차단' },
			],
			activityActions: [
				{ key: 'view', label: '조회' },
				{ key: 'print', label: '출력' },
				{ key: 'download', label: '다운로드' },
				{ key: 'create', label: '등록' },
				{ key: 'update', label: '수정' },
				{ key: 'delete', label: '삭제' },
				{ key: 'send', label: '발송' },
				{ key: 'deny', label: '권한거부' },
			],
			targetTypes: Object.keys(TARGET_LABEL).map(k => ({ key: k, label: TARGET_LABEL[k] })),
		};
	},
	created() {
		this.setRange(29);
		this.getMembers();
		this.getSummary();
	},
	methods: {
		ymd(d) {
			const p = n => String(n).padStart(2, '0');
			return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
		},
		setRange(daysBack) {
			const today = new Date();
			const start = new Date();
			start.setDate(today.getDate() - daysBack);
			this.to = this.ymd(today);
			this.from = this.ymd(start);
			// 최초 진입(created)에서는 members 가 아직 비어 있어 중복 조회를 피한다.
			if (this.members.length) this.search();
		},
		resetFilter() {
			this.memUid = '';
			this.memId = '';
			this.institution = '';
			this.ip = '';
			this.accessAction = '';
			this.activityAction = '';
			this.targetType = '';
			this.setRange(29);
			this.search();
		},
		baseQuery() {
			const p = [`from=${this.from}`, `to=${this.to}`];
			if (this.memUid) p.push(`memUid=${this.memUid}`);
			if (this.memId) p.push(`memId=${encodeURIComponent(this.memId.trim())}`);
			if (this.institution) p.push(`institution=${this.institution}`);
			if (this.ip) p.push(`ip=${encodeURIComponent(this.ip.trim())}`);
			return p.join('&');
		},
		search() {
			if (this.tab === 'summary') this.getSummary();
			else if (this.tab === 'access') this.getAccess(1);
			else this.getActivity(1);
		},
		changeTab(tab) {
			this.tab = tab;
			this.search();
		},
		drillDown(s) {
			this.memUid = s.mem_uid;
			this.memId = '';
			this.tab = 'access';
			this.getAccess(1);
		},
		getMembers() {
			this.$apiGET('/admin/api/audit/members').then(data => {
				if (data && data.st) this.members = data.list;
			});
		},
		getSummary() {
			this.loading = true;
			this.$apiGET('/admin/api/audit/summary?' + this.baseQuery())
				.then(data => {
					if (data && data.st) {
						this.summary = data.list;
						this.unknownFail = data.unknownFail || 0;
					}
				})
				.finally(() => {
					this.loading = false;
				});
		},
		getAccess(pg) {
			this.loading = true;
			const q = `${this.baseQuery()}&page=${pg}&size=${this.itemSize}` + (this.accessAction ? `&action=${this.accessAction}` : '');
			this.$apiGET('/admin/api/audit/access?' + q)
				.then(data => {
					if (data && data.st) {
						this.accessList = data.list;
						this.accessTotal = data.totalCount;
						this.accessPg = pg;
						this.accessPage = this.$pageDataSetting(data.totalCount, this.itemSize, this.blockSize, pg);
					}
				})
				.finally(() => {
					this.loading = false;
				});
		},
		getActivity(pg) {
			this.loading = true;
			let q = `${this.baseQuery()}&page=${pg}&size=${this.itemSize}`;
			if (this.activityAction) q += `&action=${this.activityAction}`;
			if (this.targetType) q += `&targetType=${this.targetType}`;
			this.$apiGET('/admin/api/audit/activity?' + q)
				.then(data => {
					if (data && data.st) {
						this.activityList = data.list;
						this.activityTotal = data.totalCount;
						this.activityPg = pg;
						this.activityPage = this.$pageDataSetting(data.totalCount, this.itemSize, this.blockSize, pg);
					}
				})
				.finally(() => {
					this.loading = false;
				});
		},
		exportCsv(kind) {
			let q = `${this.baseQuery()}&kind=${kind}`;
			if (kind === 'access' && this.accessAction) q += `&action=${this.accessAction}`;
			if (kind === 'activity') {
				if (this.activityAction) q += `&action=${this.activityAction}`;
				if (this.targetType) q += `&targetType=${this.targetType}`;
			}
			this.$apiDOWN('/admin/api/audit/export?' + q, 'text/csv').then(re => {
				if (!re) return;
				const url = window.URL.createObjectURL(new Blob([re], { type: 'text/csv;charset=utf-8;' }));
				const link = document.createElement('a');
				link.href = url;
				link.setAttribute('download', `접속활동이력_${kind === 'access' ? '접속기록' : '활동내역'}_${this.from}_${this.to}.csv`);
				document.body.appendChild(link);
				link.click();
				document.body.removeChild(link);
				window.URL.revokeObjectURL(url);
			});
		},
		canOpen(r) {
			return !!(TARGET_ROUTE[r.target_type] && r.target_uid);
		},
		openTarget(r) {
			if (!this.canOpen(r)) return;
			const w = window.screen.availWidth;
			const h = window.screen.availHeight;
			window.open(TARGET_ROUTE[r.target_type] + r.target_uid, '_blank', `width=${w}, height=${h}, resizable=no, status=no`);
		},
		actionLabel(a) {
			return ACTION_LABEL[a] || a;
		},
		targetLabel(t) {
			if (!t) return '-';
			return TARGET_LABEL[t] || t;
		},
		memberSuffix(m) {
			if (m.deleted === 'Y') return ' · 삭제된 계정';
			if (m.hire_status === 'N') return ' · 퇴사';
			return '';
		},
		instLabel(code) {
			if (!code) return '내부';
			if (code === 'shinhan') return '신한은행';
			if (code === 'daishin') return '대신증권';
			return code;
		},
		machineLabel(m) {
			if (m === 'pc') return 'PC';
			if (m === 'mobile') return '모바일';
			if (!m) return '-';
			return '기타';
		},
		fmtDt(v) {
			if (!v) return '';
			return String(v).replace('T', ' ').slice(0, 19);
		},
	},
};
</script>

<style scoped>
.tab-con {
	margin-top: 10px;
}
.btn-link {
	text-decoration: underline;
}
</style>
